
import { Helmet } from "react-helmet";
import { safeJsonStringify } from "@/lib/utils";

interface CaseStudySchemaProps {
  title: string;
  description: string;
  url: string;
  datePublished: string;
  dateModified?: string;
  industry: string;
  companyType?: string;
  challenge: string;
  solution: string;
  results: string[];
  testimonial?: {
    quote: string;
    author: string;
    role?: string;  // Made role optional to match actual usage
  };
  image?: string;
  publisher?: {
    name?: string;
    logo?: string;
  };
}

export const CaseStudySchema = ({
  title,
  description,
  url,
  datePublished,
  dateModified,
  industry,
  companyType,
  challenge,
  solution,
  results,
  testimonial,
  image = "/og-image.png",
  publisher = {
    name: "edgefi",
    logo: "/lovable-uploads/3faf62f0-dd43-43cc-82c2-9329f89d269f.png"
  }
}: CaseStudySchemaProps) => {
  const fullUrl = url.startsWith('http') ? url : `https://edgefi.com${url}`;
  const fullImage = image.startsWith('http') ? image : `https://edgefi.com${image}`;
  const fullLogo = publisher.logo?.startsWith('http') ? publisher.logo : `https://edgefi.com${publisher.logo}`;
  
  const schema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": title,
    "description": description,
    "image": fullImage,
    "datePublished": datePublished,
    "dateModified": dateModified || datePublished,
    "author": {
      "@type": "Organization",
      "name": publisher.name
    },
    "publisher": {
      "@type": "Organization",
      "name": publisher.name,
      "logo": {
        "@type": "ImageObject",
        "url": fullLogo
      }
    },
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": fullUrl
    },
    "about": [
      {
        "@type": "Thing",
        "name": `${industry} Case Study`,
        "description": `${challenge} ${solution}`
      }
    ],
    "keywords": [industry, "case study", "security transformation", companyType].filter(Boolean).join(", ")
  };
  
  // Add testimonial if provided
  if (testimonial) {
    schema["review"] = {
      "@type": "Review",
      "reviewBody": testimonial.quote,
      "author": {
        "@type": "Person",
        "name": testimonial.author,
        "jobTitle": testimonial.role || "Client" // Use "Client" as fallback if role is not provided
      }
    };
  }
  
  return (
    <Helmet>
      <script type="application/ld+json">
        {safeJsonStringify(schema)}
      </script>
    </Helmet>
  );
};
